/* ==========================================================================
   collection-filters.js
   Progressive enhancement for the native Shopify filter drawer.

   The theme's own AJAX filtering (theme.Collection / theme.AjaxRenderer) is left
   completely intact. This file only fixes two behaviours that make multi-select
   filtering unusable, without editing theme.js:

     1. theme.Collection.onFormSubmit calls FilterDrawer.close() on EVERY input,
        so the drawer slammed shut after each single selection. We temporarily
        neutralise close() for the duration of a filter input.
     2. The drawer lives inside #CollectionAjaxContent, so its DOM is replaced on
        every AJAX render and the open state is lost. We restore open state, the
        panel's scroll position and the window scroll position afterwards.

   Everything degrades safely: if theme.FilterDrawer is unavailable the page keeps
   the stock behaviour, and every hook is wrapped so a failure can never break
   the collection page.
   ========================================================================== */
(function () {
  'use strict';

  var DRAWER = '#FilterDrawer';
  var suppressClose = false;
  var pendingNewsletter = null;
  var wasOpen = false;
  var panelScroll = 0;
  var windowScroll = 0;

  function drawer() {
    return window.theme && window.theme.FilterDrawer;
  }

  function panel() {
    return document.querySelector('[data-cf-scrollable]');
  }

  function opener() {
    return document.querySelector('.js-drawer-open-collection-filters');
  }

  /* Wrap close() on the current Drawers instance. A new instance is created on
     every AJAX re-render, so this runs again after each one. */
  function wrapClose() {
    var d = drawer();
    if (!d || d.__cfWrapped) return;
    d.__cfWrapped = true;
    var original = d.close.bind(d);
    d.close = function (evt) {
      if (suppressClose) return;
      original(evt);
      if (!d.isOpen) releaseNewsletter();
    };
  }

  /* ----------------------------------------------------------------------
     Newsletter popup vs the filter drawer.

     theme.Drawers.init() binds document 'modalOpen' -> close(), and the
     newsletter popup fires modalOpen on a timer. A shopper who is mid-filter
     therefore had the drawer yanked shut from under them.

     The narrowest safe fix: while the FILTER drawer is open, defer the
     NEWSLETTER modal only, and re-open it once the drawer closes. It is
     deferred, never cancelled, so the popup still appears exactly once.

     Deliberately scoped:
       * matches ids beginning "NewsletterPopup" only, so quick view
         (QuickShopModal-*), the cart drawer, and mobile navigation are all
         untouched;
       * this file loads on collection pages only, so the patch cannot reach
         any template without a filter drawer;
       * Impulse's own modalOpen handling is left intact, so focus trap, ESC
         and focus return keep working for every modal.
     ---------------------------------------------------------------------- */
  function patchModals() {
    try {
      var M = window.theme && window.theme.Modals;
      if (!M || !M.prototype || M.prototype.__cfPatched) return;
      M.prototype.__cfPatched = true;
      var origOpen = M.prototype.open;
      M.prototype.open = function () {
        var id = this.id || '';
        if (id.indexOf('NewsletterPopup') === 0 &&
            document.querySelector(DRAWER + '.drawer--is-open')) {
          pendingNewsletter = this;
          return false;
        }
        return origOpen.apply(this, arguments);
      };
    } catch (e) { /* no-op */ }
  }

  function releaseNewsletter() {
    if (!pendingNewsletter) return;
    var m = pendingNewsletter;
    pendingNewsletter = null;
    // Wait out the drawer's 0.45s close transition so the two never overlap.
    setTimeout(function () { try { m.open(); } catch (e) { /* no-op */ } }, 600);
  }

  function remember() {
    try {
      var d = drawer();
      wasOpen = !!(d && d.isOpen);
      var p = panel();
      panelScroll = p ? p.scrollTop : 0;
      windowScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
    } catch (e) { /* no-op */ }
  }

  function restore() {
    try {
      wrapClose();
      bindApplyLabel();
      if (!wasOpen) return;

      var el = document.querySelector(DRAWER);
      if (!el) return;

      if (!el.classList.contains('drawer--is-open')) {
        var d = drawer();
        // theme.Drawers captures its element in the constructor. After an AJAX
        // render the old instance can still be holding the DETACHED node, in
        // which case open() would add the open class to nothing. Only build a
        // fresh instance when that has actually happened, so we never end up
        // with two instances bound to the same opener button.
        if (!d || !d.drawer || !document.contains(d.drawer)) {
          d = new window.theme.Drawers('FilterDrawer', 'collection-filters', true);
          window.theme.FilterDrawer = d;
          wrapClose();
        }
        // The instance's own flag can be stale (we suppressed its close), and
        // open() no-ops while it reads as open.
        d.isOpen = false;
        // Re-open against the toolbar button so focus still returns there.
        d.open(null, opener());
      }

      var p = panel();
      if (p) p.scrollTop = panelScroll;
      // theme.Collection.updateScroll() jumps the window to the grid on every
      // render; with the drawer open that reads as an unexplained page jump.
      window.scrollTo({ top: windowScroll });
    } catch (e) { /* no-op */ }
  }

  /* Capture phase: runs before theme.Collection's own bubble-phase handler,
     so close() is already neutralised by the time the theme calls it. */
  function onFilterInput(evt) {
    try {
      if (!evt.target.closest) return;
      if (!evt.target.closest(DRAWER + ' .filter-form')) return;
      remember();
      suppressClose = true;
      setTimeout(function () { suppressClose = false; }, 0);
    } catch (e) { /* no-op */ }
  }

  function onDrawerPointer(evt) {
    try {
      if (evt.target.closest && evt.target.closest(DRAWER)) remember();
    } catch (e) { /* no-op */ }
  }

  /* "View N results" simply closes the drawer — the grid behind it is already
     filtered, so there is nothing to apply and no count to fabricate. */
  function onApplyClick(evt) {
    try {
      var btn = evt.target.closest && evt.target.closest('[data-cf-apply]');
      if (!btn) return;
      evt.preventDefault();
      wasOpen = false;
      suppressClose = false;
      var d = drawer();
      if (d) d.close();
    } catch (e) { /* no-op */ }
  }

  /* Keep the drawer's aria-expanded in sync on the opener button. */
  function bindApplyLabel() {
    try {
      var btn = opener();
      var d = drawer();
      if (btn && d) btn.setAttribute('aria-expanded', d.isOpen ? 'true' : 'false');
    } catch (e) { /* no-op */ }
  }


  /* ----------------------------------------------------------------------
     Sort lives inside the drawer as its first accordion, so there is exactly
     one sort control on the page. Selecting one navigates with sort_by while
     preserving every active filter param, which is what the theme's old
     toolbar <select> did before it was removed.
     ---------------------------------------------------------------------- */
  function onSortChange(evt) {
    try {
      var input = evt.target;
      if (!input || !input.matches || !input.matches('[data-cf-sort]')) return;
      if (!input.checked) return;
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', input.value);
      params.delete('page');
      window.location.search = params.toString();
    } catch (e) { /* no-op */ }
  }

  /* Keep aria-expanded truthful. The theme's collapsible script toggles the
     is-open class; we mirror it onto the button after it has run. */
  function syncExpanded() {
    try {
      document.querySelectorAll('.cf-group__toggle').forEach(function (btn) {
        btn.setAttribute('aria-expanded', btn.classList.contains('is-open') ? 'true' : 'false');
      });
    } catch (e) { /* no-op */ }
  }

  function onAccordionClick(evt) {
    try {
      var t = evt.target.closest && evt.target.closest('.cf-group__toggle');
      if (!t) return;
      setTimeout(syncExpanded, 0);
    } catch (e) { /* no-op */ }
  }

  document.addEventListener('change', onSortChange);
  document.addEventListener('click', onAccordionClick);
  document.addEventListener('input', onFilterInput, true);
  document.addEventListener('change', onFilterInput, true);
  document.addEventListener('pointerdown', onDrawerPointer, true);
  document.addEventListener('click', onApplyClick);
  document.addEventListener('collection:reloaded', restore);
  document.addEventListener('collection:reloaded', patchModals);
  document.addEventListener('collection:reloaded', syncExpanded);
  document.addEventListener('drawerOpen', bindApplyLabel);

  function init() { wrapClose(); patchModals(); syncExpanded(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // theme.Collection and theme.Modals initialise on their own schedule; catch
  // them either way, well before the newsletter's multi-second timer fires.
  window.addEventListener('load', init);
})();
